import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Pagination,
} from '@mui/material';
import {
  getUsers,
  getDeviceInfo,
  resetDeviceRestriction,
} from '../../services/core-api';
import { Refresh as RefreshIcon, Security as SecurityIcon } from '@mui/icons-material';

interface DeviceInfo {
  user_id: number;
  username: string;
  device_fingerprint: string | null;
  device_characteristics: any;
  last_login_ip: string | null;
  last_login_at: string | null;
}

interface User {
  id: number;
  username: string;
  name: string;
  surname: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
}

const DeviceManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [deviceInfo, setDeviceInfo] = useState<{ [key: number]: DeviceInfo }>({});
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [count, setCount] = useState(0);

  const fetchUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getUsers({ page_number: page });
      setUsers(response.users);
      
      // Fetch device info for each user
      const deviceInfoMap: { [key: number]: DeviceInfo } = {};
      for (const user of response.users) {
        try {
          const deviceData = await getDeviceInfo(user.id);
          deviceInfoMap[user.id] = deviceData;
        } catch (error) {
          console.error(`Failed to fetch device info for user ${user.id}:`, error);
          deviceInfoMap[user.id] = {
            user_id: user.id,
            username: user.username,
            device_fingerprint: null,
            device_characteristics: null,
            last_login_ip: null,
            last_login_at: null,
          };
        }
      }
      setDeviceInfo(deviceInfoMap);
      setCount(Math.ceil((response.total || 0) / rowsPerPage));
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setAlert({ type: 'error', message: 'Benutzer konnten nicht geladen werden' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    setPage(newPage);
    window.scrollTo({
      top: 100,
      left: 100,
      behavior: 'smooth',
    });
  };

  const handleResetDevice = async (userId: number) => {
    try {
      await resetDeviceRestriction(userId);
      setAlert({ type: 'success', message: 'Gerätebeschränkung wurde zurückgesetzt' });
      
      // Refresh device info for this user
      const deviceData = await getDeviceInfo(userId);
      setDeviceInfo(prev => ({
        ...prev,
        [userId]: deviceData,
      }));
    } catch (error) {
      console.error('Failed to reset device restriction:', error);
      setAlert({ type: 'error', message: 'Gerätebeschränkung konnte nicht zurückgesetzt werden' });
    }
  };

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nie';
    return new Date(dateString).toLocaleString();
  };

  const getDeviceStatus = (deviceInfo: DeviceInfo) => {
    if (!deviceInfo.device_fingerprint) {
      return <Chip label="Kein Gerät" color="default" size="small" />;
    }
    return <Chip label="Gerät registriert" color="success" size="small" />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Geräteverwaltung
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUsers}
          disabled={loading}
        >
          Aktualisieren
        </Button>
      </Box>

      {alert && (
        <Alert
          severity={alert.type}
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}
        >
          {alert.message}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Benutzer</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Gerätestatus</TableCell>
              <TableCell>Letzte Anmeldung IP</TableCell>
              <TableCell>Letzte Anmeldung</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => {
              const userDeviceInfo = deviceInfo[user.id];
              return (
                <TableRow key={user.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {user.name} {user.surname}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        @{user.username}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {userDeviceInfo ? getDeviceStatus(userDeviceInfo) : 'Wird geladen …'}
                  </TableCell>
                  <TableCell>
                    {userDeviceInfo?.last_login_ip || 'k. A.'}
                  </TableCell>
                  <TableCell>
                    {userDeviceInfo ? formatDate(userDeviceInfo.last_login_at) : 'k. A.'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Gerätedetails anzeigen">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(user)}
                        >
                          <SecurityIcon />
                        </IconButton>
                      </Tooltip>
                      {userDeviceInfo?.device_fingerprint && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => handleResetDevice(user.id)}
                        >
                          Gerät zurücksetzen
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
          <Pagination
            count={count}
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={handlePageChange}
          />
        </Box>
      </Stack>

      {/* Device Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Gerätedetails – {selectedUser?.name} {selectedUser?.surname}
        </DialogTitle>
        <DialogContent>
          {selectedUser && deviceInfo[selectedUser.id] && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Geräteinformationen
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Geräte-Fingerprint
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {deviceInfo[selectedUser.id].device_fingerprint || 'Nicht registriert'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Letzte Anmeldung IP
                </Typography>
                <Typography variant="body2">
                  {deviceInfo[selectedUser.id].last_login_ip || 'k. A.'}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Zeitpunkt letzte Anmeldung
                </Typography>
                <Typography variant="body2">
                  {formatDate(deviceInfo[selectedUser.id].last_login_at)}
                </Typography>
              </Box>

              {deviceInfo[selectedUser.id].device_characteristics && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    Geräteeigenschaften
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      backgroundColor: 'grey.100',
                      p: 2,
                      borderRadius: 1,
                      fontSize: '0.875rem',
                      overflow: 'auto',
                      maxHeight: 200,
                    }}
                  >
                    {JSON.stringify(deviceInfo[selectedUser.id].device_characteristics, null, 2)}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Schliessen</Button>
          {selectedUser && deviceInfo[selectedUser.id]?.device_fingerprint && (
            <Button
              color="warning"
              onClick={() => {
                handleResetDevice(selectedUser.id);
                setDialogOpen(false);
              }}
            >
              Gerät zurücksetzen
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceManagement; 